// @TS-nocheck
import PropTypes, { object, string } from 'prop-types'
import styled from 'styled-components'

import EnquiryModal from '@components/EnquiryModal'
import cssClasses from '@constants/cssClasses'
import { Separator } from '@components'
import { mq, formatArtworkTitle, px2rem, hiDPIpx, getSiteLanguage } from '@utils'
import ArtworksArtistList from './ArtworksArtistList'
import ArtworksGalleryList from './ArtworksGalleryList'
import ArtworkTitle from './Title'
import ArtworkDescription from './Description'
import ArtworksImages from './ArtworksImages'
import { useLabelsFromAPI } from '@dmi-mch/hooks'
import tokens from '@mch-group/design-tokens/build/js/design_tokens-module'
import { Container, Row, Col } from '@mch-group/uikit-components'

const mID = 'artworks-detail'

const ArtworkLayoutCatalog = ({
  pageMetaInfo,
  notAvailableOptionLabel,
  costDisclaimer,
  attachments,
  reservedArtworkOptionLabel,
  ...props
}) => {
  const { labels = {} } = useLabelsFromAPI(['ShowRoomArtwork', 'AltTags'], getSiteLanguage())
  const { ShowRoomArtwork } = labels || {}
  return (
    <ArtworkDetailWrapper className={`${mID} ${cssClasses.CMS_MODULE}`}>
      <Container className={`px-5 px-lg-8 px-md-7 ${mID}__description-wrapper`}>
        <Row>
          <Col className={`${mID}__title`}>
            <ArtworkTitle {...pageMetaInfo} />
          </Col>
        </Row>
        <Row>
          <Col lg={7} className={`${mID}__images`}>
            {pageMetaInfo && pageMetaInfo.images && (
              <ArtworksImages
                images={pageMetaInfo.images}
                displayName={pageMetaInfo.displayName}
                downloadLabel={props.det_grp1_download_label}
                artistName={pageMetaInfo.artistName}
                legacyImage={{
                  small: {
                    url: pageMetaInfo.imageUrl,
                    width: pageMetaInfo.imageWidth,
                    height: pageMetaInfo.imageHeight
                  },
                  // some artworks dont have largeImageWidth and largeImageHeight
                  // this will make the plugin to not be able to scale and zoom the image correctly
                  // so we falback to the min image size in the old portfolio manager
                  large: {
                    url: pageMetaInfo.largeSizeImageUrl,
                    width: pageMetaInfo.largeImageWidth || 2048,
                    height: pageMetaInfo.largeImageHeight || 2048
                  }
                }}
                video={
                  pageMetaInfo.videoId && {
                    videoId: pageMetaInfo.videoId,
                    videoType: pageMetaInfo.videoType,
                    videoPosition: typeof pageMetaInfo.videoPosition === 'undefined' ? 1 : pageMetaInfo.videoPosition
                  }
                }
              />
            )}
          </Col>
          <Col lg={5} className={`${mID}__description`}>
            <ArtworkDescription
              {...pageMetaInfo}
              attachments={attachments}
              labels={{
                min: props.det_grp1_minute_label,
                cm: props.det_grp1_label_size_cm,
                inch: props.det_grp1_label_size_in,
                kg: props.det_grp1_kilogram_label,
                m3: props.det_grp1_cubic_metre_label,
                notAvailable: notAvailableOptionLabel,
                costDisclaimer: costDisclaimer,
                artworkSizeLabel: ShowRoomArtwork?.artworkSizeLabel,
                artworkSizeWithFrameLabel: ShowRoomArtwork?.artworkSizeWithFrameLabel,
                reservedArtworkLabel: reservedArtworkOptionLabel
              }}
            />
            <EnquiryModal
              image={pageMetaInfo.images && pageMetaInfo.images[0]?.url}
              title={formatArtworkTitle(pageMetaInfo.displayName, pageMetaInfo.year, pageMetaInfo.endYear)}
              imgAlt={`${pageMetaInfo.displayName} ${labels?.AltTags?.altTagBy} ${pageMetaInfo.artistName} ${pageMetaInfo.year ? `(${pageMetaInfo.year})` : ''}`}
              subtitle={pageMetaInfo.artistName}
              id={pageMetaInfo.id}
              eventId={pageMetaInfo.eventId || null}
              showInRoom={pageMetaInfo.showInRoom || null}
              // Configuration coming from the CMS module (Artwork Detail DET)
              showWhenCatalog={props.contact_catalog_artwork === 'true'}
              showWhenExhibition={props.contact_exhib_artwork === 'true'}
              showWhenShowroom={props.contact_show_artwork === 'true'}
              contactBtnClass='contact-sales-inquiry'
            />
          </Col>
        </Row>
      </Container>
      <ArtworksArtistList
        linkProfile={props.det_grp1_tab1_link}
        moreFromLabel={props.det_grp1_tab1_title}
        {...pageMetaInfo}
      />
      <Separator className={`${mID}__separator`} />
      <ArtworksGalleryList
        linkProfile={props.det_grp1_tab2_link}
        moreFromLabel={props.det_grp1_tab2_title}
        {...pageMetaInfo}
      />
    </ArtworkDetailWrapper>
  )
}

const ArtworkDetailWrapper = styled.section`
  &&& {
    margin-top: ${px2rem(75)};
    ${mq.between('tablet', 'largestTablet')`
      margin-top: ${hiDPIpx(104)};
    `}
    ${mq.lessThan('largestMobile')`
      margin-top: ${px2rem(104)};
    `}

    .${mID} {
      ${mq.lessThan('largestTablet')`
        &__title { order: 2; }
        &__images { order: 1; }
        &__description { order: 3; }
        &__separator { display: none; }
      `}

      &__description-wrapper {
        display: flex;
        flex-direction: column;
        ${mq.between('tablet', 'largestTablet')`
          margin-bottom: ${hiDPIpx(40)};
        `}
        ${mq.lessThan('largestMobile')`
          margin-bottom: ${px2rem(40)};
        `}
      }

      &__description {
        button {
          font-weight: ${tokens.fontWeights.artBaselHeadline0.value};
          ${mq.greaterThan('desktop')`
            width: ${px2rem(310)};
          `}
          ${mq.lessThan('largestMobile')`
            width: 100%;
          `}
        }
      }
    }
  }
`

ArtworkLayoutCatalog.propTypes = {
  notAvailableOptionLabel: string,
  costDisclaimer: string,
  pageMetaInfo: object.isRequired,
  det_grp1_download_label: string,
  det_grp1_minute_label: string,
  det_grp1_label_size_cm: string,
  det_grp1_label_size_in: string,
  det_grp1_kilogram_label: string,
  det_grp1_cubic_metre_label: string,
  det_grp1_tab1_link: string,
  det_grp1_tab1_title: string,
  det_grp1_tab2_link: string,
  det_grp1_tab2_title: string,
  contact_catalog_artwork: string,
  contact_exhib_artwork: string,
  contact_show_artwork: string,
  labels: object,
  attachments: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  reservedArtworkOptionLabel: string
}

export default ArtworkLayoutCatalog
